import React, { useState } from 'react';
import { CheckCircle, ArrowRight, Calculator, BookOpen, HeartPulse, ShieldCheck, Landmark, Users } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const professions = [
  { id: 'cpa', label: 'CPA / Accountant', icon: <Calculator className="h-5 w-5" /> },
  { id: 'bookkeeper', label: 'Bookkeeper', icon: <BookOpen className="h-5 w-5" /> },
  { id: 'medical_biller', label: 'Medical Biller', icon: <HeartPulse className="h-5 w-5" /> },
  { id: 'insurance_agent', label: 'Insurance Agent', icon: <ShieldCheck className="h-5 w-5" /> },
  { id: 'loan_broker', label: 'Loan Broker', icon: <Landmark className="h-5 w-5" /> },
  { id: 'other', label: 'Other Professional', icon: <Users className="h-5 w-5" /> },
];

const interests = [
  'Earn additional revenue from client referrals',
  'Help my clients access better financing',
  'Diversify my service offerings',
  'Build a long term referral partnership',
  'Explore the Cashflow Engineering model',
];

const PartnerFunnel = () => {
  const [step, setStep] = useState(1);
  const [selectedProfession, setSelectedProfession] = useState('');
  const [selectedInterest, setSelectedInterest] = useState('');
  const [form, setForm] = useState({ full_name: '', email: '', phone: '', company: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/funnel/partner`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, profession: selectedProfession, interest: selectedInterest }),
      });
      if (res.ok) setDone(true);
    } catch (err) {
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-[#0A1628] flex items-center justify-center px-4" style={{ fontFamily: 'var(--font-body)' }}>
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 right-1/3 w-[500px] h-[500px] bg-amber-500/6 rounded-full blur-[150px]" />
          <div className="absolute bottom-1/4 left-1/4 w-[400px] h-[400px] bg-teal-500/6 rounded-full blur-[120px]" />
        </div>
        <div data-testid="partner-funnel-success" className="relative text-center max-w-md">
          <div className="w-20 h-20 mx-auto mb-8 rounded-full bg-amber-500/20 flex items-center justify-center">
            <CheckCircle className="h-10 w-10 text-amber-400" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: 'var(--font-heading)' }}>Welcome to the Team</h1>
          <p className="text-slate-400 text-base leading-relaxed mb-8">
            A Quidity partnership manager will reach out within 24 hours to get you started as a Cashflow Engineer. Get ready to unlock a new revenue stream.
          </p>
          <a href="https://quidity.fund" className="text-sm text-amber-400 hover:text-amber-300 transition-colors">Visit Quidity Direct</a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A1628] relative overflow-hidden" style={{ fontFamily: 'var(--font-body)' }}>
      {/* Background effects */}
      <div className="absolute inset-0">
        <div className="absolute top-0 right-1/4 w-[600px] h-[600px] bg-amber-500/5 rounded-full blur-[180px]" />
        <div className="absolute bottom-0 left-1/3 w-[500px] h-[500px] bg-teal-500/5 rounded-full blur-[150px]" />
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
          backgroundSize: '60px 60px'
        }} />
      </div>

      <div className="relative z-10 max-w-2xl mx-auto px-5 py-12">
        {/* Logo */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-3 mb-2">
            <img src="https://customer-assets.emergentagent.com/job_0073d843-75f0-4073-b1ec-cb9427d27521/artifacts/vnbckdjp_QuidityDirect%20Logo.png" alt="Quidity" className="h-8 brightness-0 invert" />
          </div>
          <p className="text-amber-400 text-xs font-semibold uppercase tracking-[0.2em]">Become a Cashflow Engineer</p>
        </div>

        {/* Progress */}
        <div className="flex items-center gap-2 justify-center mb-10">
          {[1, 2, 3].map((s) => (
            <div key={s} className={`h-1.5 rounded-full transition-all duration-500 ${s <= step ? 'bg-amber-400 w-16' : 'bg-white/10 w-8'}`} />
          ))}
        </div>

        {/* STEP 1: Profession Selection */}
        {step === 1 && (
          <div className="animate-fade-in">
            <h1 className="text-3xl sm:text-4xl font-bold text-white text-center mb-3 leading-tight" style={{ fontFamily: 'var(--font-heading)' }}>
              What's Your Profession?
            </h1>
            <p className="text-slate-400 text-center mb-10 text-base">Select the role that best describes you. We tailor partnerships for each.</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {professions.map((p) => (
                <button
                  key={p.id}
                  data-testid={`profession-${p.id}`}
                  onClick={() => { setSelectedProfession(p.id); setStep(2); }}
                  className={`text-left p-5 rounded-xl border transition-all duration-300 group hover:scale-[1.02] ${
                    selectedProfession === p.id
                      ? 'border-amber-400 bg-amber-500/10'
                      : 'border-white/10 bg-white/[0.03] hover:border-amber-400/50 hover:bg-white/[0.06]'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2.5 rounded-lg transition-colors ${selectedProfession === p.id ? 'bg-amber-400/20 text-amber-300' : 'bg-white/5 text-slate-400 group-hover:text-amber-400'}`}>
                      {p.icon}
                    </div>
                    <div className="text-white font-semibold text-sm">{p.label}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* STEP 2: Interest + Company */}
        {step === 2 && (
          <div className="animate-fade-in">
            <h1 className="text-3xl sm:text-4xl font-bold text-white text-center mb-3 leading-tight" style={{ fontFamily: 'var(--font-heading)' }}>
              What Interests You Most?
            </h1>
            <p className="text-slate-400 text-center mb-10 text-base">This helps us personalize your partner experience.</p>

            <div className="space-y-5">
              <div className="space-y-2.5">
                {interests.map((i) => (
                  <button
                    key={i}
                    data-testid={`interest-${i.slice(0, 15).replace(/\s/g, '-')}`}
                    onClick={() => setSelectedInterest(i)}
                    className={`w-full text-left p-4 rounded-xl border text-sm transition-all ${
                      selectedInterest === i
                        ? 'border-amber-400 bg-amber-500/10 text-amber-300'
                        : 'border-white/10 bg-white/[0.03] text-slate-400 hover:border-amber-400/40 hover:text-white'
                    }`}
                  >
                    {i}
                  </button>
                ))}
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Your Company / Practice Name</label>
                <input
                  data-testid="partner-company"
                  type="text"
                  value={form.company}
                  onChange={(e) => setForm({ ...form, company: e.target.value })}
                  className="w-full bg-white/[0.05] border border-white/10 rounded-xl px-5 py-4 text-white placeholder-slate-600 text-sm focus:outline-none focus:border-amber-400/50 focus:bg-white/[0.07] transition-all"
                  placeholder="Smith & Associates CPA"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Anything Else? (Optional)</label>
                <textarea
                  data-testid="partner-message"
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  rows={3}
                  className="w-full bg-white/[0.05] border border-white/10 rounded-xl px-5 py-4 text-white placeholder-slate-600 text-sm focus:outline-none focus:border-amber-400/50 focus:bg-white/[0.07] transition-all resize-none"
                  placeholder="Number of clients, current referral setup, etc."
                />
              </div>
            </div>

            <div className="flex gap-3 mt-8">
              <button onClick={() => setStep(1)} className="px-6 py-4 rounded-xl border border-white/10 text-slate-400 text-sm font-medium hover:text-white hover:border-white/20 transition-all">Back</button>
              <button
                data-testid="partner-step2-next"
                onClick={() => { if (selectedInterest) setStep(3); }}
                disabled={!selectedInterest}
                className="flex-1 py-4 rounded-xl bg-amber-500 text-white text-sm font-semibold hover:bg-amber-400 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {/* STEP 3: Contact Info */}
        {step === 3 && (
          <div className="animate-fade-in">
            <h1 className="text-3xl sm:text-4xl font-bold text-white text-center mb-3 leading-tight" style={{ fontFamily: 'var(--font-heading)' }}>
              Let's Get You Started
            </h1>
            <p className="text-slate-400 text-center mb-10 text-base">Enter your details and we'll have a partnership manager reach out within 24 hours.</p>

            <div className="space-y-5">
              <div>
                <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Full Name</label>
                <input
                  data-testid="partner-full-name"
                  type="text"
                  value={form.full_name}
                  onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                  className="w-full bg-white/[0.05] border border-white/10 rounded-xl px-5 py-4 text-white placeholder-slate-600 text-sm focus:outline-none focus:border-amber-400/50 focus:bg-white/[0.07] transition-all"
                  placeholder="Jane Doe"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Email</label>
                <input
                  data-testid="partner-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full bg-white/[0.05] border border-white/10 rounded-xl px-5 py-4 text-white placeholder-slate-600 text-sm focus:outline-none focus:border-amber-400/50 focus:bg-white/[0.07] transition-all"
                  placeholder="jane@cpagroup.com"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Phone</label>
                <input
                  data-testid="partner-phone"
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full bg-white/[0.05] border border-white/10 rounded-xl px-5 py-4 text-white placeholder-slate-600 text-sm focus:outline-none focus:border-amber-400/50 focus:bg-white/[0.07] transition-all"
                  placeholder="(555) 123 4567"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-8">
              <button onClick={() => setStep(2)} className="px-6 py-4 rounded-xl border border-white/10 text-slate-400 text-sm font-medium hover:text-white hover:border-white/20 transition-all">Back</button>
              <button
                data-testid="partner-submit"
                onClick={handleSubmit}
                disabled={!form.full_name || !form.email || !form.phone || submitting}
                className="flex-1 py-4 rounded-xl bg-amber-500 text-white text-sm font-semibold hover:bg-amber-400 transition-all disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {submitting ? 'Submitting...' : 'Become a Cashflow Engineer'} {!submitting && <ArrowRight className="h-4 w-4" />}
              </button>
            </div>

            <p className="text-center text-xs text-slate-600 mt-6">We respect your privacy. No spam, ever.</p>
          </div>
        )}

        {/* Trust bar */}
        <div className="mt-16 pt-8 border-t border-white/5">
          <div className="grid grid-cols-3 gap-4 text-center">
            {[
              { value: '$2B+', label: 'Capital Deployed' },
              { value: '1,000+', label: 'Referral Partners' },
              { value: '98%', label: 'Partner Satisfaction' },
            ].map((s) => (
              <div key={s.label}>
                <div className="text-lg font-bold text-white" style={{ fontFamily: 'var(--font-heading)' }}>{s.value}</div>
                <div className="text-[10px] text-slate-500 uppercase tracking-wider mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PartnerFunnel;
